import React, {useEffect, useRef} from 'react'
import AdyenCheckout from '@adyen/adyen-web'
import '@adyen/adyen-web/dist/adyen.css'
import {Spinner, Flex} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getCurrencyValueForApi} from '../../../adyenOverrides/utils/parsers.mjs'
import {AdyenPaymentsService} from '../../../adyenOverrides/services/payments'
import {AdyenShippingMethodsService} from '../../../adyenOverrides/services/shipping-methods'
import {AdyenShippingAddressService} from '../../../adyenOverrides/services/shipping-address'
import {PAYMENT_METHODS} from '../../../adyenOverrides/utils/constants.mjs'
import {getApplePayStyle} from './index.style'
import useAdyenExpressCheckout from '../../../adyenOverrides/hooks/useAdyenExpressCheckout'

const PAYMENT_METHOD = PAYMENT_METHODS.APPLE_PAY

export const getApplePaymentMethodConfig = (paymentMethodsResponse) => {
    const applePayPaymentMethod = paymentMethodsResponse?.paymentMethods?.find(
        (pm) => pm.type === PAYMENT_METHOD
    )
    return applePayPaymentMethod?.configuration || null
}

export const getCustomerShippingDetails = (shippingContact) => {
    return {
        deliveryAddress: {
            city: shippingContact.locality,
            country: shippingContact.countryCode,
            houseNumberOrName:
                shippingContact.addressLines?.length > 1 ? shippingContact.addressLines[1] : '',
            postalCode: shippingContact.postalCode,
            stateOrProvince: shippingContact.administrativeArea,
            street: shippingContact.addressLines?.[0]
        },
        profile: {
            firstName: shippingContact.givenName,
            lastName: shippingContact.familyName,
            email: shippingContact.emailAddress,
            phone: shippingContact.phoneNumber
        }
    }
}

export const getCustomerBillingDetails = (billingContact) => {
    return {
        billingAddress: {
            city: billingContact.locality,
            country: billingContact.countryCode,
            houseNumberOrName:
                billingContact?.addressLines?.length > 1 ? billingContact.addressLines[1] : '',
            postalCode: billingContact.postalCode,
            stateOrProvince: billingContact.administrativeArea,
            street: billingContact.addressLines?.[0]
        }
    }
}

export const getShippingMethods = (basket, shippingMethods) => {
    if (basket?.c_isBopisBossGCOnly || basket?.c_isStorePickup) {
        const {id, description, name, price} = basket.shipments[0].shippingMethod
        return [
            {
                label: name,
                detail: description,
                identifier: id,
                amount: parseFloat(price).toFixed(2)
            }
        ]
    } else {
        return shippingMethods?.map((sm) => ({
            label: sm.name,
            detail: sm.description,
            identifier: sm.id,
            amount: parseFloat(sm.price).toFixed(2)
        }))
    }
}

export const getAppleButtonConfig = (
    authToken,
    site,
    basket,
    shippingMethods,
    adyenPaymentMethods,
    navigate,
    fetchShippingMethods
) => {
    const applePayConfig = getApplePaymentMethodConfig(adyenPaymentMethods)
    let applePayAmount = basket.orderTotal
    const shippingRequiredFields = ['name', 'email', 'phone']
    const buttonConfig = {
        showPayButton: true,
        buttonType: 'order',
        isExpress: true,
        configuration: applePayConfig,
        countryCode: 'US',
        amount: {
            value: getCurrencyValueForApi(basket.orderTotal, basket.currency),
            currency: basket.currency
        },
        requiredShippingContactFields: [
            ...(!basket.c_isStorePickup ? ['postalAddress'] : []),
            ...shippingRequiredFields
        ],
        requiredBillingContactFields: ['postalAddress'],
        shippingMethods: getShippingMethods(basket, shippingMethods),
        onAuthorized: async (resolve, reject, event) => {
            try {
                const {shippingContact, billingContact, token} = event.payment
                let custShippingDetails = getCustomerShippingDetails(shippingContact)
                if (basket?.c_isStorePickup) {
                    custShippingDetails.deliveryAddress = null
                }
                const state = {
                    data: {
                        paymentType: 'express',
                        paymentMethod: {
                            type: PAYMENT_METHOD,
                            applePayToken: token.paymentData
                        },
                        ...getCustomerBillingDetails(billingContact),
                        ...custShippingDetails
                    }
                }
                const adyenPaymentService = new AdyenPaymentsService(authToken, site)
                const paymentsResponse = await adyenPaymentService.submitPayment(
                    {
                        ...state.data,
                        origin: state.data.origin ? state.data.origin : window.location.origin
                    },
                    basket?.basketId,
                    basket?.customerInfo?.customerId
                )
                if (paymentsResponse?.isFinal && paymentsResponse?.isSuccessful) {
                    const finalPriceUpdate = {
                        newTotal: {
                            type: 'final',
                            label: applePayConfig.merchantName,
                            amount: `${applePayAmount}`
                        }
                    }
                    resolve(finalPriceUpdate)
                    navigate(`/checkout/confirmation/${paymentsResponse?.merchantReference}`)
                } else {
                    reject()
                }
            } catch (err) {
                reject()
            }
        },
        onSubmit: () => {},
        onShippingContactSelected: async (resolve, reject, event) => {
            try {
                if (basket?.c_isBopisBossGCOnly || basket?.c_isStorePickup) {
                    reject()
                } else {
                    const {shippingContact} = event
                    const adyenShippingAddressService = new AdyenShippingAddressService(
                        authToken,
                        site
                    )
                    const adyenShippingMethodsService = new AdyenShippingMethodsService(
                        authToken,
                        site
                    )
                    const customerShippingDetails = getCustomerShippingDetails(shippingContact)
                    if (
                        customerShippingDetails &&
                        customerShippingDetails.deliveryAddress &&
                        customerShippingDetails.deliveryAddress.country !== 'US'
                    ) {
                        reject(
                            new Error(
                                'Invalid shipping address and Shipping is available within the US only.'
                            )
                        )
                    }
                    await adyenShippingAddressService.updateShippingAddress(
                        basket.basketId,
                        customerShippingDetails
                    )
                    const newShippingMethods = await fetchShippingMethods(
                        basket?.basketId,
                        site,
                        authToken
                    )
                    if (!newShippingMethods?.applicableShippingMethods?.length) {
                        reject()
                    } else {
                        const response = await adyenShippingMethodsService.updateShippingMethod(
                            newShippingMethods.applicableShippingMethods[0].id,
                            basket.basketId
                        )
                        buttonConfig.amount = {
                            value: getCurrencyValueForApi(response.orderTotal, response.currency),
                            currency: response.currency
                        }
                        applePayAmount = response.orderTotal
                        const finalPriceUpdate = {
                            newShippingMethods: newShippingMethods?.applicableShippingMethods?.map(
                                (sm) => ({
                                    label: sm.name,
                                    detail: sm.description,
                                    identifier: sm.id,
                                    amount: `${sm.price}`
                                })
                            ),
                            newTotal: {
                                type: 'final',
                                label: applePayConfig.merchantName,
                                amount: `${applePayAmount}`
                            }
                        }
                        resolve(finalPriceUpdate)
                    }
                }
            } catch (err) {
                reject()
            }
        },
        onShippingMethodSelected: async (resolve, reject, event) => {
            try {
                const {shippingMethod} = event
                const adyenShippingMethodsService = new AdyenShippingMethodsService(authToken, site)
                const response = await adyenShippingMethodsService.updateShippingMethod(
                    shippingMethod.identifier,
                    basket.basketId
                )
                if (response.error) {
                    reject()
                } else {
                    buttonConfig.amount = {
                        value: getCurrencyValueForApi(response.orderTotal, response.currency),
                        currency: response.currency
                    }
                    applePayAmount = response.orderTotal
                    const applePayShippingMethodUpdate = {
                        newTotal: {
                            type: 'final',
                            label: applePayConfig.merchantName,
                            amount: `${applePayAmount}`
                        }
                    }
                    resolve(applePayShippingMethodUpdate)
                }
            } catch (err) {
                reject()
            }
        }
    }
    return buttonConfig
}

const ApplePayExpressComponent = (props) => {
    const {
        adyenEnvironment,
        adyenPaymentMethods,
        basket,
        locale,
        site,
        authToken,
        navigate,
        shippingMethods,
        fetchShippingMethods,
        isMiniCart
    } = useAdyenExpressCheckout()
    const paymentContainer = useRef(null)

    useEffect(() => {
        const createCheckout = async () => {
            try {
                const shippingMethodsData =
                    shippingMethods ||
                    (props.shippingMethods
                        ? props.shippingMethods
                        : await fetchShippingMethods(basket?.basketId, site, authToken))
                const checkout = await AdyenCheckout({
                    environment: adyenEnvironment?.ADYEN_ENVIRONMENT,
                    clientKey: adyenEnvironment?.ADYEN_CLIENT_KEY,
                    locale: locale.id,
                    analytics: {
                        analyticsData: {
                            applicationInfo: adyenPaymentMethods?.applicationInfo
                        }
                    }
                })
                const appleButtonConfig = getAppleButtonConfig(
                    authToken,
                    site,
                    basket,
                    shippingMethodsData?.applicableShippingMethods,
                    adyenPaymentMethods,
                    navigate,
                    fetchShippingMethods
                )
                const applePayButton = await checkout.create(PAYMENT_METHOD, appleButtonConfig)
                const isApplePayButtonAvailable = await applePayButton.isAvailable()
                if (isApplePayButtonAvailable && appleButtonConfig.configuration) {
                    applePayButton.mount(paymentContainer.current)
                }
            } catch (err) {
                // Error
            }
        }
        if (
            adyenEnvironment &&
            adyenPaymentMethods &&
            basket &&
            shippingMethods &&
            paymentContainer.current
        ) {
            createCheckout()
        }
    }, [adyenEnvironment, adyenPaymentMethods, shippingMethods])

    return (
        <>
            {props.showLoading && (
                <Flex align={'center'} justify={'center'}>
                    <Spinner size={'lg'} mt={4} />
                </Flex>
            )}
            <Flex
                justifyContent={'center'}
                sx={getApplePayStyle(isMiniCart)}
                ref={paymentContainer}
            ></Flex>
        </>
    )
}

ApplePayExpressComponent.propTypes = {
    showLoading: PropTypes.bool,
    shippingMethods: PropTypes.array
}

export default ApplePayExpressComponent
